import React, { useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './ChangeRequestImportButton.less';
import { createChangeRequest } from '../../../app/ActionTypes/changeRequestActionTypes';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../app/store';
import { useAgentDispatch } from '../../../hooks/useAgentStore';
import { unwrapResult } from '@reduxjs/toolkit';
import { Notify } from '../../../services/notificationService';
import {
  getReadonlyElementTitle,
  isUserReadonly,
} from '../../../services/subscription';
import { getAppLoaderController } from '../../../hooks/loadingContext';
import { fetchChangeRequests } from '../../../app/ActionTypes/changeRequestsActions';
import ActionButton from '../../ActionButton/ActionButton';
import { selectSubscriptionStatus } from '../../../app/Slices/subscriptionSlice';

interface ChangeRequestImportButtonProps {
  label: string;
  disabled?: boolean;
}

const ChangeRequestImportButton: React.FC<ChangeRequestImportButtonProps> = ({
  label,
  disabled = false,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const agentDispatch = useAgentDispatch();
  const status = useSelector(selectSubscriptionStatus);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // const { myChangeRequests } = useSelector(
  //   (state: RootState) => state.changeRequestItems
  // );
  const isReadonly = isUserReadonly(status);

  const onImportChangeRequest = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.type !== 'application/json') {
      Notify.error('Please select a JSON file.');
      return;
    }

    const { setAppLoading } = getAppLoaderController();
    try {
      setAppLoading(true);
      let importData;
      try {
        const importDataStr = await file.text();
        importData = JSON.parse(importDataStr);
      } catch (error) {
        Notify.error('Invalid JSON file');
        return;
      }

      if (!importData.change_request?.summary) {
        Notify.error('Invalid JSON file');
        return;
      }

      try {
        const result = await importChangeRequest({
          ...importData.change_request,
          source: importData.source,
        });

        if (result && result?.changeRequestId) {
          agentDispatch(fetchChangeRequests());
          navigate(`/change-requests/${result?.changeRequestId}`, {
            replace: true,
          });
        }
      } catch (error) {
        Notify.error('Unable to import Change Request');
      }
    } finally {
      setAppLoading(false);
    }
  };

  const importChangeRequest = async (importData: Record<string, unknown>) => {
    try {
      const createResponse = await dispatch(
        createChangeRequest({
          ...importData,
        })
      );
      const {
        data: { id },
      } = unwrapResult(createResponse);

      if (!id) {
        Notify.warning('Failed to import Change Request!');
        return;
      }

      Notify.success('Change Request imported successfully.');
      return { changeRequestId: id };
    } catch (error) {
      return null;
    }
  };

  return (
    <div className="change-request-import-button">
      <ActionButton
        label={label}
        onClick={onImportChangeRequest}
        disabled={isReadonly || disabled}
        disabledMessage={getReadonlyElementTitle()}
      />
      <input
        className="change-request-import-button-file-input"
        type="file"
        style={{ display: 'none' }}
        accept=".json,application/json"
        ref={fileInputRef}
        onChange={handleFileChange}
      />
    </div>
  );
};

export default ChangeRequestImportButton;
